/** UXP-00014 — AI Automation Studio framework (mirrors pkg/automationstudio) */

export const UXP_AS_ID = 'UXP-00014';

export interface WorkforceKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface AgentRow {
  type: string;
  name: string;
  status: string;
  tasks: number;
  success: number;
}

export interface AgentConfigRow {
  name: string;
  capabilities: number;
  tools: number;
  scope: string;
  mode: string;
}

export interface WorkflowRow {
  name: string;
  steps: number;
  triggers: string;
  status: string;
  success: number;
}

export interface TriggerRow {
  type: string;
  name: string;
  events: number;
  status: string;
}

export interface BusinessRuleRow {
  feature: string;
  name: string;
  priority: number;
  active: boolean;
}

export interface MultiAgentRow {
  title: string;
  agents: string;
  status: string;
  progress: number;
}

export interface HumanLoopRow {
  title: string;
  mode: string;
  agent: string;
  priority: string;
}

export const WORKFORCE_METRICS = [
  'Agent Health', 'Workflow Health', 'Execution Status', 'Automation KPIs',
  'Business Impact', 'Credits', 'Resource Usage', 'Automation ROI',
];

export const AGENT_TYPES = [
  'Executive Agents', 'Marketing Agents', 'Catalog Agents', 'Creative Agents',
  'Advertising Agents', 'Inventory Agents', 'Finance Agents', 'Customer Success Agents',
  'Compliance Agents', 'Marketplace Agents', 'Custom Agents',
];

export const AGENT_BUILDER_FEATURES = [
  'Agent Identity', 'Capabilities', 'Knowledge Sources', 'Allowed Tools',
  'Execution Scope', 'Decision Limits', 'Approval Rules', 'Escalation Rules',
  'Priority', 'Memory',
];

export const WORKFLOW_DESIGNER_FEATURES = [
  'Visual Workflow Design', 'Triggers', 'Conditions', 'Loops', 'Parallel Execution',
  'Approvals', 'Notifications', 'Retries', 'Rollback', 'Scheduling',
];

export const TRIGGER_TYPES = [
  'Marketplace Events', 'Schedules', 'Webhooks', 'API Calls', 'Customer Events',
  'Inventory Events', 'Financial Events', 'Manual Triggers', 'Custom Events',
];

export const BUSINESS_RULE_FEATURES = [
  'Conditional Logic', 'Business Policies', 'AI Policies', 'Marketplace Policies',
  'Organization Policies', 'Brand Policies', 'Priority Rules',
];

export const MULTI_AGENT_FEATURES = [
  'Task Delegation', 'Cross-agent Communication', 'Shared Context',
  'Dependency Management', 'Coordinated Execution',
];

export const HUMAN_LOOP_MODES = [
  'Suggestion Mode', 'Approval Mode', 'Autonomous Mode',
  'Emergency Override', 'Role-based Approval',
];

export const KNOWLEDGE_SOURCES = [
  'Brand DNA', 'Product DNA', 'Customer Intent DNA', 'Growth DNA', 'Supply Chain DNA',
  'CX DNA', 'Financial DNA', 'Decision DNA', 'Prompt Library', 'Knowledge Base',
];

export const AGENT_MEMORY_FEATURES = [
  'Execution History', 'Reasoning History', 'Performance', 'Feedback',
  'Learning', 'Decision Outcomes',
];

export const AUTOMATION_ANALYTICS = [
  'Hours Saved', 'Cost Saved', 'Automation Success', 'Workflow Success',
  'Agent Effectiveness', 'Business ROI',
];

export const SECURITY_FEATURES = [
  'Permission Boundaries', 'Secret Management', 'Sandbox Execution',
  'Audit Logging', 'Execution Isolation',
];

export const SIMULATION_FEATURES = [
  'Workflow Outcomes', 'Business Impact', 'Credits', 'Revenue', 'Risk', 'Execution Time',
];

export const COLLABORATION_FEATURES = [
  'Assignments', 'Comments', 'Approvals', 'Escalations', 'Mentions', 'Review Queues',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'AI Workforce Command Center', 'AI Agent Library', 'Agent Builder', 'Workflow Designer',
  'Trigger Engine', 'Business Rule Engine', 'Multi-Agent Collaboration', 'Human-in-the-Loop',
];

export const LIVE_ROUTES: Record<string, string> = {
  workflows: '/workflows',
  copilot: '/copilot',
  aix: '/platform/aix',
  knowledge: '/knowledge',
};

export const DEMO_KPIS: WorkforceKPI[] = [
  { label: 'Agent Health', value: '96%', change: '+2%', positive: true },
  { label: 'Workflow Health', value: '94%', change: '+1.5%', positive: true },
  { label: 'Execution Status', value: '142 active', change: '8 queued', positive: true },
  { label: 'Automation KPIs', value: '88/100', change: '+5', positive: true },
  { label: 'Business Impact', value: '₹12.4L saved/mo', change: '+18%', positive: true },
  { label: 'Credits', value: '84,200', change: '62% used', positive: true },
  { label: 'Resource Usage', value: '72%', change: 'within budget', positive: true },
  { label: 'Automation ROI', value: '4.6x', change: '+0.4x', positive: true },
];

export const DEMO_AGENTS: AgentRow[] = [
  { type: 'Catalog Agents', name: 'Listing Optimizer Agent', status: 'active', tasks: 842, success: 96 },
  { type: 'Advertising Agents', name: 'PPC Bid Manager Agent', status: 'active', tasks: 1240, success: 91 },
  { type: 'Inventory Agents', name: 'Replenishment Planner Agent', status: 'active', tasks: 320, success: 94 },
  { type: 'Finance Agents', name: 'Settlement Reconciler Agent', status: 'active', tasks: 156, success: 98 },
  { type: 'Customer Success Agents', name: 'Returns Resolution Agent', status: 'paused', tasks: 89, success: 87 },
  { type: 'Compliance Agents', name: 'Policy Compliance Agent', status: 'active', tasks: 412, success: 99 },
];

export const DEMO_AGENT_CONFIGS: AgentConfigRow[] = [
  { name: 'Listing Optimizer Agent', capabilities: 8, tools: 12, scope: 'catalog · content', mode: 'approval' },
  { name: 'PPC Bid Manager Agent', capabilities: 6, tools: 9, scope: 'advertising · campaigns', mode: 'autonomous' },
  { name: 'Replenishment Planner Agent', capabilities: 7, tools: 6, scope: 'inventory · supply chain', mode: 'approval' },
];

export const DEMO_WORKFLOWS: WorkflowRow[] = [
  { name: 'Prime Day Listing Prep', steps: 14, triggers: 'schedule · manual', status: 'running', success: 94 },
  { name: 'Low Stock Auto-Reorder', steps: 8, triggers: 'inventory event', status: 'active', success: 97 },
  { name: 'Settlement Reconciliation', steps: 11, triggers: 'financial event', status: 'active', success: 99 },
  { name: 'Return Approval Pipeline', steps: 6, triggers: 'customer event', status: 'active', success: 88 },
];

export const DEMO_TRIGGERS: TriggerRow[] = [
  { type: 'Inventory Events', name: 'Stock below reorder point', events: 1240, status: 'active' },
  { type: 'Marketplace Events', name: 'Amazon order placed', events: 8420, status: 'active' },
  { type: 'Schedules', name: 'Daily settlement sync', events: 31, status: 'active' },
  { type: 'Financial Events', name: 'Settlement variance detected', events: 18, status: 'active' },
  { type: 'Webhooks', name: 'TallyPrime invoice posted', events: 412, status: 'active' },
];

export const DEMO_RULES: BusinessRuleRow[] = [
  { feature: 'Business Policies', name: 'No price change >10% without approval', priority: 95, active: true },
  { feature: 'AI Policies', name: 'LLM spend cap ₹5K/day per tenant', priority: 90, active: true },
  { feature: 'Marketplace Policies', name: 'Amazon policy compliance gate', priority: 98, active: true },
  { feature: 'Brand Policies', name: 'Brand voice consistency check', priority: 85, active: true },
];

export const DEMO_MULTI_AGENT: MultiAgentRow[] = [
  { title: 'Prime Day campaign launch', agents: 'PPC · Catalog · Creative', status: 'in_progress', progress: 72 },
  { title: 'Q3 inventory optimization', agents: 'Inventory · Finance · Supply', status: 'planning', progress: 34 },
  { title: 'Settlement dispute resolution', agents: 'Finance · Compliance', status: 'completed', progress: 100 },
];

export const DEMO_HUMAN_LOOP: HumanLoopRow[] = [
  { title: 'Approve 5% price increase on Hub Gen 3', mode: 'Approval Mode', agent: 'Pricing Agent', priority: 'high' },
  { title: 'Review PPC bid adjustment (+₹2K/day)', mode: 'Suggestion Mode', agent: 'PPC Bid Manager', priority: 'medium' },
  { title: 'Emergency pause — compliance flag on listing', mode: 'Emergency Override', agent: 'Compliance Agent', priority: 'critical' },
];

export function buildAutomationStudioDashboard() {
  return {
    workforce_metrics: WORKFORCE_METRICS.length,
    agent_types: AGENT_TYPES.length,
    builder_features: AGENT_BUILDER_FEATURES.length,
    workflow_features: WORKFLOW_DESIGNER_FEATURES.length,
    trigger_types: TRIGGER_TYPES.length,
  };
}
